import { useCallback, useEffect, useState } from 'react';
import { addMonths, startOfMonth } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import type { supabase as supabaseClient } from '../lib/supabase';
import { countNewLeadsCreatedBetween } from '../lib/crm/countNewLeads';
import { getBusinessLeadCreatedAtRanges } from '../lib/crm/leadCreatedAtRanges';
import {
  loadCrmNoActivitySettings,
  isLeadStaleNoActivity,
  hasScheduledFollowUp,
  type CrmNoActivitySettings,
} from '../lib/crm/noActivitySettings';
import { fetchLatestMessageActivityByCustomer } from '../lib/crm/recentMessageActivity';

interface WonDealRow {
  value: number | null;
  created_at: string;
  updated_at: string;
}

interface OpenOppRow {
  value: number | null;
  customer_id: string | null;
  created_at: string;
  next_follow_up_at: string | null;
  last_activity_at: string | null;
}

/** Outbound activity types that count as "first contact" with a lead. */
const CONTACT_ACTIVITY_TYPES = ['sms_sent', 'email_sent', 'phone_call'];

/** Won opportunities that were *created* in a period (cohort for conversion rate). */
async function countWonCreatedBetween(
  supabase: typeof supabaseClient,
  businessId: string,
  createdFromInclusive: string,
  createdBeforeExclusive: string
): Promise<number> {
  const { count, error } = await supabase
    .from('pipeline_opportunities')
    .select('id', { count: 'exact', head: true })
    .eq('business_id', businessId)
    .eq('status', 'Won')
    .not('created_at', 'is', null)
    .gte('created_at', createdFromInclusive)
    .lt('created_at', createdBeforeExclusive);

  if (error) {
    throw error;
  }

  return count ?? 0;
}

/**
 * Conversion on a MATURED cohort: leads created 30–90 days ago that are now
 * Won. This is the honest probability a lead ever turns into money — unlike win
 * rate (won ÷ closed) it counts leads that just sit in the pipeline forever, and
 * unlike this month's cohort it gives leads time to actually close.
 */
async function fetchMaturedCohortConversion(
  supabase: typeof supabaseClient,
  businessId: string
): Promise<{ rate: number; cohortSize: number; won: number }> {
  const now = Date.now();
  const cohortStart = new Date(now - 90 * 86_400_000).toISOString();
  const cohortEnd = new Date(now - 30 * 86_400_000).toISOString();

  const base = () =>
    supabase
      .from('pipeline_opportunities')
      .select('id', { count: 'exact', head: true })
      .eq('business_id', businessId)
      .not('created_at', 'is', null)
      .gte('created_at', cohortStart)
      .lt('created_at', cohortEnd);

  const [{ count: cohortSize, error: cohortError }, { count: won, error: wonError }] =
    await Promise.all([base(), base().eq('status', 'Won')]);

  if (cohortError) throw cohortError;
  if (wonError) throw wonError;

  const size = cohortSize ?? 0;
  const wonCount = won ?? 0;
  return {
    rate: size > 0 ? (wonCount / size) * 100 : 0,
    cohortSize: size,
    won: wonCount,
  };
}

/**
 * Average won deal value over a trailing window (180 days). A single month can
 * be a handful of deals, which swings the average wildly; the trailing window
 * keeps it steady enough to multiply against.
 */
async function fetchTrailingAvgWonValue(
  supabase: typeof supabaseClient,
  businessId: string
): Promise<number> {
  const since = new Date(Date.now() - 180 * 86_400_000).toISOString();
  const { data, error } = await supabase
    .from('pipeline_opportunities')
    .select('value')
    .eq('business_id', businessId)
    .eq('status', 'Won')
    .gt('value', 0)
    .gte('updated_at', since);

  if (error) throw error;
  const values = (data ?? []).map((row) => Number(row.value) || 0).filter((v) => v > 0);
  return mean(values);
}

/**
 * Average days for a lead to be RESOLVED (won or lost) over a trailing window.
 * Using won-only understates it badly — fast wins dominate while slow deals are
 * still sitting open — so both outcomes count here.
 */
async function fetchTrailingResolutionDays(
  supabase: typeof supabaseClient,
  businessId: string
): Promise<number> {
  const since = new Date(Date.now() - 180 * 86_400_000).toISOString();
  const { data, error } = await supabase
    .from('pipeline_opportunities')
    .select('created_at, updated_at')
    .eq('business_id', businessId)
    .in('status', ['Won', 'Lost'])
    .gte('updated_at', since);

  if (error) throw error;
  const days = (data ?? [])
    .map((row) => {
      const created = new Date(row.created_at).getTime();
      const resolved = new Date(row.updated_at).getTime();
      if (Number.isNaN(created) || Number.isNaN(resolved) || resolved < created) return null;
      return (resolved - created) / 86_400_000;
    })
    .filter((d): d is number => d != null);
  return mean(days);
}

/** Leads marked Lost in a period (status change updates updated_at in CRM). */
async function countLeadsMarkedLost(
  supabase: typeof supabaseClient,
  businessId: string,
  monthStartInclusive: string,
  monthEndExclusive: string
): Promise<number> {
  const { count, error } = await supabase
    .from('pipeline_opportunities')
    .select('id', { count: 'exact', head: true })
    .eq('business_id', businessId)
    .eq('status', 'Lost')
    .gte('updated_at', monthStartInclusive)
    .lt('updated_at', monthEndExclusive);

  if (error) {
    throw error;
  }

  return count ?? 0;
}

/** Leads marked Lost this period that were CREATED before it — the portion of
 *  the opening balance that ended in Lost (needed for the ledger math). */
async function countCarriedLeadsMarkedLost(
  supabase: typeof supabaseClient,
  businessId: string,
  monthStartInclusive: string,
  monthEndExclusive: string
): Promise<number> {
  const { count, error } = await supabase
    .from('pipeline_opportunities')
    .select('id', { count: 'exact', head: true })
    .eq('business_id', businessId)
    .eq('status', 'Lost')
    .gte('updated_at', monthStartInclusive)
    .lt('updated_at', monthEndExclusive)
    .lt('created_at', monthStartInclusive);

  if (error) {
    throw error;
  }

  return count ?? 0;
}

/**
 * Won deals in a period with value + timestamps, so we can derive count,
 * average deal size, and average time-to-close from one query.
 */
async function fetchWonDealsBetween(
  supabase: typeof supabaseClient,
  businessId: string,
  startInclusive: string,
  endExclusive: string
): Promise<WonDealRow[]> {
  const { data, error } = await supabase
    .from('pipeline_opportunities')
    .select('value, created_at, updated_at')
    .eq('business_id', businessId)
    .eq('status', 'Won')
    .gte('updated_at', startInclusive)
    .lt('updated_at', endExclusive);

  if (error) {
    throw error;
  }

  return (data ?? []) as WonDealRow[];
}

/**
 * All currently-open opportunities with the fields needed to derive open count,
 * pipeline value, overdue follow-ups, and stalled (no-activity) deals — in one query.
 */
async function fetchOpenOpportunities(
  supabase: typeof supabaseClient,
  businessId: string
): Promise<OpenOppRow[]> {
  const { data, error } = await supabase
    .from('pipeline_opportunities')
    .select('value, customer_id, created_at, next_follow_up_at, last_activity_at')
    .eq('business_id', businessId)
    .eq('status', 'Open');

  if (error) {
    throw error;
  }

  return (data ?? []) as OpenOppRow[];
}

/**
 * Stalled deals per last_activity_at, minus any lead whose customer actually
 * has a recent message in all_messages (inbound webhooks, automations, and
 * campaigns don't write opportunity_activities, so the column under-counts).
 * Keeps this metric consistent with the Control Centre actions list.
 */
async function countStalledDeals(
  supabase: typeof supabaseClient,
  businessId: string,
  openOpps: OpenOppRow[],
  settings: CrmNoActivitySettings,
  now: Date
): Promise<number> {
  if (!settings.enabled) return 0;

  const staleCandidates = openOpps.filter(
    (opp) =>
      !hasScheduledFollowUp(opp.next_follow_up_at, now) &&
      isLeadStaleNoActivity(opp.last_activity_at, opp.created_at, settings.hours, now)
  );
  if (staleCandidates.length === 0) return 0;

  const customerIds = [
    ...new Set(staleCandidates.map((opp) => opp.customer_id).filter((id): id is string => !!id)),
  ];
  if (customerIds.length === 0) return staleCandidates.length;

  try {
    const { data: customers, error } = await supabase
      .from('customers')
      .select('id, phone, email')
      .in('id', customerIds);
    if (error) throw error;

    const sinceIso = new Date(now.getTime() - settings.hours * 60 * 60 * 1000).toISOString();
    const latestByCustomer = await fetchLatestMessageActivityByCustomer(
      supabase,
      businessId,
      customers ?? [],
      sinceIso
    );

    return staleCandidates.filter(
      (opp) => !(opp.customer_id && latestByCustomer.has(opp.customer_id))
    ).length;
  } catch (err) {
    // Fall back to the column-only count rather than breaking all metrics.
    console.error('Error reconciling stalled deals with messages:', err);
    return staleCandidates.length;
  }
}

function sumValue(rows: Array<{ value: number | null }>): number {
  return rows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
}

function mean(nums: number[]): number {
  if (nums.length === 0) return 0;
  return nums.reduce((acc, n) => acc + n, 0) / nums.length;
}

function avgDealSize(rows: WonDealRow[]): number {
  return mean(rows.map((r) => Number(r.value) || 0));
}

/** Average days between lead creation and being marked won. */
function avgTimeToCloseDays(rows: WonDealRow[]): number {
  const days = rows
    .map((r) => {
      const created = new Date(r.created_at).getTime();
      const closed = new Date(r.updated_at).getTime();
      if (Number.isNaN(created) || Number.isNaN(closed) || closed < created) {
        return null;
      }
      return (closed - created) / 86_400_000;
    })
    .filter((d): d is number => d != null);
  return mean(days);
}

function winRate(won: number, lost: number): number {
  const closed = won + lost;
  return closed > 0 ? (won / closed) * 100 : 0;
}

/**
 * Average days between a lead being created and the first outbound contact
 * (SMS / email / phone) logged against it. Cohort = leads created in the
 * period; only contacted leads count toward the average.
 */
async function fetchAvgTimeToFirstContactDays(
  supabase: typeof supabaseClient,
  businessId: string,
  createdFromInclusive: string,
  createdBeforeExclusive: string
): Promise<number> {
  const { data: opps, error: oppError } = await supabase
    .from('pipeline_opportunities')
    .select('id, created_at')
    .eq('business_id', businessId)
    .not('created_at', 'is', null)
    .gte('created_at', createdFromInclusive)
    .lt('created_at', createdBeforeExclusive);

  if (oppError) {
    throw oppError;
  }
  if (!opps || opps.length === 0) return 0;

  const createdMsById = new Map<number, number>();
  for (const opp of opps) {
    const created = new Date(opp.created_at).getTime();
    if (!Number.isNaN(created)) createdMsById.set(opp.id, created);
  }

  const ids = Array.from(createdMsById.keys());
  if (ids.length === 0) return 0;

  const { data: activities, error: actError } = await supabase
    .from('opportunity_activities')
    .select('pipeline_opportunities_id, created_at')
    .eq('business_id', businessId)
    .in('pipeline_opportunities_id', ids)
    .in('activity_type', CONTACT_ACTIVITY_TYPES)
    .order('created_at', { ascending: true });

  if (actError) {
    throw actError;
  }
  if (!activities || activities.length === 0) return 0;

  // activities are sorted ascending, so the first one seen per opp is earliest.
  const firstContactMsById = new Map<number, number>();
  for (const act of activities) {
    const oppId = act.pipeline_opportunities_id as number;
    if (firstContactMsById.has(oppId)) continue;
    const contacted = new Date(act.created_at).getTime();
    if (!Number.isNaN(contacted)) firstContactMsById.set(oppId, contacted);
  }

  const deltas: number[] = [];
  firstContactMsById.forEach((contactedMs, oppId) => {
    const createdMs = createdMsById.get(oppId);
    if (createdMs == null) return;
    const days = (contactedMs - createdMs) / 86_400_000;
    if (days >= 0) deltas.push(days);
  });

  return mean(deltas);
}

function getMonthRanges(timezone: string) {
  const zonedNow = toZonedTime(new Date(), timezone);
  const { monthStart, nextMonthStart } = getBusinessLeadCreatedAtRanges(timezone);
  const lastMonthStart = fromZonedTime(
    startOfMonth(addMonths(zonedNow, -1)),
    timezone
  ).toISOString();

  return { monthStart, nextMonthStart, lastMonthStart };
}

const ZERO_METRICS = {
  newLeadsThisMonth: 0,
  openLeads: 0,
  /** Leads that were open when this month started (the ledger's opening
   *  balance): openingBalance + newThisMonth = openNow + wonThisMonth + lostThisMonth. */
  openingBalanceLeads: 0,
  leadsWonThisMonth: 0,
  leadsLostThisMonth: 0,
  newLeadsLastMonth: 0,
  leadsWonLastMonth: 0,
  leadsLostLastMonth: 0,
  openPipelineValue: 0,
  avgWonDealSize: 0,
  avgWonDealSizeLastMonth: 0,
  avgTimeToCloseDays: 0,
  avgTimeToCloseDaysLastMonth: 0,
  avgTimeToFirstContactDays: 0,
  avgTimeToFirstContactDaysLastMonth: 0,
  winRateThisMonth: 0,
  winRateLastMonth: 0,
  revenueWonThisMonth: 0,
  revenueWonLastMonth: 0,
  conversionRateThisMonth: 0,
  conversionRateLastMonth: 0,
  conversionWonThisMonth: 0,
  /** Honest close probability: leads created 30–90 days ago that are now Won. */
  maturedConversionRate: 0,
  maturedCohortSize: 0,
  /** Trailing 180-day averages — stable enough to drive the velocity maths. */
  avgWonDealSizeTrailing: 0,
  avgResolutionDaysTrailing: 0,
  overdueFollowUpsCount: 0,
  stalledDealsCount: 0,
  /** Whether the CRM "no activity" alert is enabled — gates the stalled metric. */
  noActivityEnabled: false,
};

type CrmLeadMetricsState = typeof ZERO_METRICS;

export type CrmLeadMetrics = CrmLeadMetricsState & {
  loading: boolean;
  refresh: () => Promise<void>;
};

/** CRM lead counts + pipeline-health KPIs for the control-centre widget header. */
export function useCrmLeadMetrics() {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timezone =
    selectedBusiness?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    'UTC';

  const [metrics, setMetrics] = useState<CrmLeadMetricsState>(ZERO_METRICS);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!supabase || !businessId) {
      setMetrics(ZERO_METRICS);
      setLoading(false);
      return;
    }

    try {
      const { monthStart, nextMonthStart, lastMonthStart } = getMonthRanges(timezone);

      const [
        newThisMonth,
        newLastMonth,
        openOpps,
        wonThisRows,
        lostThisMonth,
        wonLastRows,
        lostLastMonth,
        timeToFirstContactThis,
        timeToFirstContactLast,
        wonCreatedThisMonth,
        wonCreatedLastMonth,
        noActivitySettings,
        maturedConversion,
        avgWonDealSizeTrailing,
        avgResolutionDaysTrailing,
      ] = await Promise.all([
        countNewLeadsCreatedBetween(supabase, businessId, monthStart, nextMonthStart),
        countNewLeadsCreatedBetween(supabase, businessId, lastMonthStart, monthStart),
        fetchOpenOpportunities(supabase, businessId),
        fetchWonDealsBetween(supabase, businessId, monthStart, nextMonthStart),
        countLeadsMarkedLost(supabase, businessId, monthStart, nextMonthStart),
        fetchWonDealsBetween(supabase, businessId, lastMonthStart, monthStart),
        countLeadsMarkedLost(supabase, businessId, lastMonthStart, monthStart),
        fetchAvgTimeToFirstContactDays(supabase, businessId, monthStart, nextMonthStart),
        fetchAvgTimeToFirstContactDays(supabase, businessId, lastMonthStart, monthStart),
        countWonCreatedBetween(supabase, businessId, monthStart, nextMonthStart),
        countWonCreatedBetween(supabase, businessId, lastMonthStart, monthStart),
        loadCrmNoActivitySettings(supabase, businessId),
        fetchMaturedCohortConversion(supabase, businessId),
        fetchTrailingAvgWonValue(supabase, businessId),
        fetchTrailingResolutionDays(supabase, businessId),
      ]);
      const carriedLostThisMonth = await countCarriedLeadsMarkedLost(
        supabase,
        businessId,
        monthStart,
        nextMonthStart
      );

      const wonThisMonth = wonThisRows.length;
      const wonLastMonth = wonLastRows.length;
      const now = new Date();

      const overdueFollowUpsCount = openOpps.filter((opp) => {
        if (!opp.next_follow_up_at) return false;
        const due = new Date(opp.next_follow_up_at).getTime();
        return !Number.isNaN(due) && due <= now.getTime();
      }).length;

      const stalledDealsCount = await countStalledDeals(
        supabase,
        businessId,
        openOpps,
        noActivitySettings,
        now
      );

      // Opening balance = leads open when the month started: those still open
      // now + those the month resolved (won/lost) that were created earlier.
      const monthStartMs = new Date(monthStart).getTime();
      const createdBeforeMonth = (createdAt: string | null | undefined) => {
        if (!createdAt) return false;
        const created = new Date(createdAt).getTime();
        return !Number.isNaN(created) && created < monthStartMs;
      };
      const carriedStillOpen = openOpps.filter((opp) => createdBeforeMonth(opp.created_at)).length;
      const carriedWonThisMonth = wonThisRows.filter((row) => createdBeforeMonth(row.created_at)).length;
      const openingBalanceLeads = carriedStillOpen + carriedWonThisMonth + carriedLostThisMonth;

      setMetrics({
        newLeadsThisMonth: newThisMonth,
        newLeadsLastMonth: newLastMonth,
        openLeads: openOpps.length,
        openingBalanceLeads,
        leadsWonThisMonth: wonThisMonth,
        leadsLostThisMonth: lostThisMonth,
        leadsWonLastMonth: wonLastMonth,
        leadsLostLastMonth: lostLastMonth,
        openPipelineValue: sumValue(openOpps),
        avgWonDealSize: avgDealSize(wonThisRows),
        avgWonDealSizeLastMonth: avgDealSize(wonLastRows),
        avgTimeToCloseDays: avgTimeToCloseDays(wonThisRows),
        avgTimeToCloseDaysLastMonth: avgTimeToCloseDays(wonLastRows),
        avgTimeToFirstContactDays: timeToFirstContactThis,
        avgTimeToFirstContactDaysLastMonth: timeToFirstContactLast,
        winRateThisMonth: winRate(wonThisMonth, lostThisMonth),
        winRateLastMonth: winRate(wonLastMonth, lostLastMonth),
        revenueWonThisMonth: sumValue(wonThisRows),
        revenueWonLastMonth: sumValue(wonLastRows),
        conversionRateThisMonth: newThisMonth > 0 ? (wonCreatedThisMonth / newThisMonth) * 100 : 0,
        conversionRateLastMonth: newLastMonth > 0 ? (wonCreatedLastMonth / newLastMonth) * 100 : 0,
        conversionWonThisMonth: wonCreatedThisMonth,
        maturedConversionRate: maturedConversion.rate,
        maturedCohortSize: maturedConversion.cohortSize,
        avgWonDealSizeTrailing,
        avgResolutionDaysTrailing,
        overdueFollowUpsCount,
        stalledDealsCount,
        noActivityEnabled: noActivitySettings.enabled,
      });
    } catch (err) {
      console.error('Error loading CRM lead metrics:', err);
      setMetrics(ZERO_METRICS);
    } finally {
      setLoading(false);
    }
  }, [supabase, businessId, timezone]);

  useEffect(() => {
    setLoading(true);
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [load]);

  return {
    ...metrics,
    loading,
    refresh: load,
  };
}
